/**
 * Reglas de la ventana de ajuste de recursos.
 *
 * Deciden cuándo un reporte lee sus recursos EN VIVO del registro del
 * contratista y cuándo usa los que tiene congelados.
 */

import type { Rd } from "@app-types/entities";

/** ¿La ventana de ajuste está abierta ahora? */
export function ajusteAbierto(rd: Pick<Rd, "ajusteRecursos">): boolean {
  const a = rd.ajusteRecursos;
  return !!a && !a.cerradoEn;
}

/**
 * ¿Los recursos de este reporte se leen EN VIVO del registro?
 *
 * En vivo mientras el reporte no esté aprobado —lo que el contratista corrija
 * se ve de inmediato— y también con la ventana de ajuste abierta.
 *
 * Un reporte aprobado sin ventana usa lo CONGELADO: es lo que hace que su PDF
 * firmado siga valiendo cuando el contratista siga registrando días nuevos.
 */
export function recursosEnVivo(rd: Pick<Rd, "estado" | "ajusteRecursos">): boolean {
  return rd.estado !== "APROBADO" || ajusteAbierto(rd);
}

/**
 * ¿Puede el contratista tocar los recursos de este día?
 *
 * Sí mientras el reporte no esté aprobado, y sí con la ventana abierta. Un
 * reporte aprobado y sin autorización queda intocable — que es el punto de
 * congelarlo.
 *
 * Ojo: esto gobierna el REGISTRO de ese día para esa OT, no el módulo entero.
 * El contratista sigue pudiendo registrar días nuevos con toda libertad.
 */
export function puedeAjustarRecursos(
  rd: Pick<Rd, "estado" | "ajusteRecursos"> | null | undefined,
): boolean {
  if (!rd) return true; // aún no hay reporte de ese día: nada que proteger
  return recursosEnVivo(rd);
}

/** Texto del estado de la ventana, para la banda del reporte. */
export function descripcionAjuste(rd: Pick<Rd, "ajusteRecursos">): string {
  const a = rd.ajusteRecursos;
  if (!a) return "";
  if (!a.cerradoEn) {
    return `Ajuste de recursos autorizado por ${a.autorizadoPor}: ${a.motivo}`;
  }
  return `Recursos ajustados y re-congelados el ${a.cerradoEn.slice(0, 10)} por ${a.cerradoPor ?? "—"}.`;
}
